import React from 'react';
import { Plus } from 'lucide-react';
import { Camera, MapPin, Upload } from 'lucide-react';
import { Asset } from '../../lib/types';

interface AssetDetailsFormProps {
  formData: Omit<Asset, 'id' | 'uid'>;
  setFormData: React.Dispatch<React.SetStateAction<Omit<Asset, 'id' | 'uid'>>>;
  imageFile: File | null;
  setImageFile: React.Dispatch<React.SetStateAction<File | null>>;
  previewUrl: string | null;
  setPreviewUrl: React.Dispatch<React.SetStateAction<string | null>>;
  isSubmitting: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  editingAsset?: Asset | null;
}

export function AssetDetailsForm({
  formData,
  setFormData,
  setImageFile,
  previewUrl,
  setPreviewUrl,
  isSubmitting,
  handleSubmit,
  fileInputRef,
  editingAsset,
}: AssetDetailsFormProps) {
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const assetTypes = [
    'Real Estate',
    'Vehicle',
    'Electronics',
    'Jewelry',
    'Investment',
    'Cash',
    'Other',
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Image Upload */}
      <div className="flex justify-center">
        <div
          onClick={() => fileInputRef.current?.click()}
          className="group relative flex h-40 w-full cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 transition-all hover:border-indigo-500 dark:border-zinc-950"
        >
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt="Asset"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="text-center text-white">
                  <Camera className="mx-auto mb-1 h-6 w-6" />
                  <p className="text-xs font-bold">Change Photo</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <Upload className="h-6 w-6 text-zinc-400" />
              <p className="mt-2 text-xs text-zinc-500">Upload a photo</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
          Asset Name
        </label>
        <input
          required
          placeholder="e.g. House in Lekki"
          className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
          Type
        </label>
        <select
          required
          className="w-full appearance-none rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
        >
          {assetTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
            Estimated Value
          </label>
          <input
            type="number"
            placeholder="0.00"
            step="0.01"
            required
            className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
            value={formData.value}
            onChange={(e) =>
              setFormData({
                ...formData,
                value: parseFloat(e.target.value) || 0,
              })
            }
          />
        </div>
        <div className="space-y-1.5">
          <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
            Location
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Optional"
              className="w-full rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 pl-9 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
            />
            <MapPin className="absolute top-3.5 left-3 h-4 w-4 text-zinc-400" />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 font-bold text-white shadow-lg transition-opacity hover:opacity-90 active:scale-[0.98] disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
      >
        {isSubmitting ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : (
          <>
            {editingAsset ? (
              'Save Changes'
            ) : (
              <>
                <Plus className="h-4 w-4" /> Add Asset
              </>
            )}
          </>
        )}
      </button>
    </form>
  );
}
